import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useClubs } from "../hooks/useClubs";
import { distanceKm, hasValidLocation } from "../lib/geo";
import { AppFooter } from "../components/AppFooter";

type LocState = "idle" | "locating" | "granted" | "denied" | "unsupported";

const MIN_RADIUS = 5;
const MAX_RADIUS = 150;

export function ClubSelect() {
  const { clubs, loading, error } = useClubs();
  const [search, setSearch] = useState("");
  const [locState, setLocState] = useState<LocState>("idle");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(50);

  function requestLocation() {
    if (!("geolocation" in navigator)) {
      setLocState("unsupported");
      return;
    }
    setLocState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocState("granted");
      },
      () => setLocState("denied"),
      { enableHighAccuracy: false, timeout: 10000 }
    );
  }

  const withDistance = useMemo(() => {
    return clubs.map((c) => {
      const valid = hasValidLocation(c.location?.latitude ?? 0, c.location?.longitude ?? 0);
      const distance =
        coords && valid ? distanceKm(coords.lat, coords.lng, c.location.latitude, c.location.longitude) : null;
      return { club: c, distance };
    });
  }, [clubs, coords]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = withDistance.filter(({ club }) => !term || club.name.toLowerCase().includes(term));

    if (coords) {
      list = list.filter(({ distance }) => distance === null || distance <= radiusKm);
      list = [...list].sort((a, b) => {
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
    }
    return list;
  }, [withDistance, search, coords, radiusKm]);

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-10 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-turf-900">Club auswählen</h1>
      <p className="mt-1 text-sm text-ink-400">Wähle deinen Golfclub, um loszulegen.</p>

      <div className="relative mt-5">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="pointer-events-none absolute left-4 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-ink-400"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
        <label htmlFor="club-search" className="sr-only">
          Club suchen
        </label>
        <input
          id="club-search"
          type="search"
          placeholder="Club suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12 w-full rounded-xl border border-chalk-200 bg-white pl-11 pr-4 text-base outline-none transition-colors focus:border-turf-600"
        />
      </div>

      {/* Distance bar */}
      <div className="card mt-3 rounded-2xl p-4">
        {locState !== "granted" ? (
          <button
            type="button"
            onClick={requestLocation}
            disabled={locState === "locating"}
            className="flex w-full items-center gap-3 text-left disabled:opacity-60"
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-turf-100 text-turf-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-5 w-5">
                <path d="M12 21s7-6.5 7-11.5a7 7 0 1 0-14 0C5 14.5 12 21 12 21ZM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900">
                {locState === "locating" ? "Standort wird ermittelt…" : "Clubs in deiner Nähe anzeigen"}
              </span>
              <span className="block text-xs text-ink-400">
                {locState === "denied"
                  ? "Zugriff verweigert – in den Browser-Einstellungen erlauben und erneut versuchen."
                  : locState === "unsupported"
                  ? "Dein Browser unterstützt keine Standortabfrage."
                  : "Zeigt Entfernung an und sortiert danach."}
              </span>
            </span>
          </button>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="radius" className="text-sm font-semibold text-ink-900">
                Umkreis
              </label>
              <span className="font-display text-base font-semibold text-turf-800">
                {radiusKm >= MAX_RADIUS ? `${MAX_RADIUS}+ km` : `${radiusKm} km`}
              </span>
            </div>
            <input
              id="radius"
              type="range"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={5}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="mt-2 h-2 w-full appearance-none rounded-full bg-chalk-200 accent-turf-700"
            />
            <p className="mt-1 text-xs text-ink-400">Clubs außerhalb des Umkreises werden ausgeblendet.</p>
          </div>
        )}
      </div>

      {loading && (
        <ul className="mt-5 space-y-3" aria-label="Clubs werden geladen">
          {[0, 1, 2].map((i) => (
            <li key={i} className="card flex items-center gap-4 rounded-2xl p-4">
              <div className="skeleton h-12 w-12 flex-shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="skeleton h-4 w-2/3 rounded" />
                <div className="skeleton h-3 w-1/2 rounded" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-8 rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">
          {error}
        </p>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="mt-10 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-turf-100 text-turf-700">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-6 w-6">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" strokeLinecap="round" />
            </svg>
          </div>
          <p className="mt-3 font-medium text-ink-900">Keine Clubs gefunden</p>
          <p className="mt-1 text-sm text-ink-400">
            {coords ? "Versuch's mit einem größeren Umkreis oder anderem Suchbegriff." : "Versuch's mit einem anderen Suchbegriff."}
          </p>
        </div>
      )}

      <ul className="mt-5 space-y-3">
        {filtered.map(({ club, distance }, i) => (
          <li key={club.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <Link
              to={`/club/${club.id}`}
              className="card card-interactive flex items-center gap-4 rounded-2xl p-4"
            >
              {club.logo ? (
                <img src={club.logo} alt="" className="h-12 w-12 flex-shrink-0 rounded-full object-cover" />
              ) : (
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-turf-100 font-display text-lg text-turf-700">
                  {club.name.charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink-900">{club.name}</p>
                <p className="truncate text-sm text-ink-400">{club.contact?.address}</p>
              </div>
              {distance !== null && (
                <span className="marker-badge h-7 flex-shrink-0 border-0 bg-turf-100 px-2.5 text-xs text-turf-700">
                  {distance < 1 ? "< 1 km" : `${distance.toFixed(0)} km`}
                </span>
              )}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 flex-shrink-0 text-ink-400">
                <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </li>
        ))}
      </ul>
      <AppFooter />
    </main>
  );
}
